import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { usePageTracking, useMetricsIdentity } from "@byscott-io/metricsbyscott-web";
import {
  configureAuth,
  AuthProvider,
  ThemeProvider,
  useAuth,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  AcceptInvitationPage,
  ContainersPage,
  ProtectedRoute as AuthProtectedRoute,
  PublicRoute,
  MarketingRoute,
  PublicChat,
  useSpaLinkInterceptor,
  ErrorBoundary,
  UpdateAvailableBanner,
  VERSION_ANNOUNCED_EVENT,
} from '@byscott-io/corebyscott';
import { configureCable, PresenceProvider, useWaldoEvent } from '@byscott-io/where-is-waldo';
import { BillingProvider, configure as configureBilling } from "@byscott-io/billingbyscott_client";
import {
  WidgetStyles,
  WidgetsProvider,
  Dashboard as WidgetsDashboard,
  WidgetDetail as WidgetDetailView,
} from "@byscott-io/widgetsbyscott-client";
import { storage } from "@byscott-io/corebyscott";
import Layout from "./components/Layout";

// Pages
import Dashboard from "./pages/Dashboard";
import Events from "./pages/Events";
import Values from "./pages/Values";
import ReferralFunnel from "./pages/ReferralFunnel";
import Visitors from "./pages/Visitors";
import Explore from "./pages/Explore";
import Apps from "./pages/Apps";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import SupportPage from "./pages/SupportPage";

// Marketing pages
import LandingPage from "./pages/marketing/LandingPage";
import PricingPage from "./pages/marketing/PricingPage";
import HelpPage from "./pages/marketing/HelpPage";
import AboutPage from "./pages/marketing/AboutPage";

// Configure corebyscott
// WhereIsWaldo cable connection. Configured once at module load, like the other
// clients here. The token is read lazily so a reconnect after a refresh picks
// up the current one rather than whatever was in storage at boot.
configureCable({
  url: '/cable',
  getToken: () => storage.getToken(),
  presence: {
    debug: false,
  },
});

configureAuth({
  appName: "MetricsByScott",
  containerName: "account",
  containerLabel: "Account",
  containerLabelPlural: "Accounts",
  containerHeader: "X-Account-Id",
  containerBasePath: "/app",
  dashboardPath: "/app",
  loginPath: "/login",
  registerPath: "/register",
  roles: ["owner", "admin", "viewer"],
  adminRoles: ["owner", "admin"],
  features: {
    invitations: true,
    passwordReset: true,
    sessions: true,
    multiContainer: true,
    registration: true,
    mfa: true,
  },
  jwtIncludeContainer: true,
  // Bot-check config for anonymous chat (#521) — read from layout meta tags.
  botCheckProvider:
    typeof document !== "undefined"
      ? document.querySelector('meta[name="bot-check-provider"]')?.content || "off"
      : "off",
  turnstileSiteKey:
    typeof document !== "undefined"
      ? document.querySelector('meta[name="turnstile-site-key"]')?.content || null
      : null,
  footer: {
    enabled: true,
    brand: "IdeasByScott",
    brandUrl: "https://byscott.io",
    links: [],
  },
  marketing: {
    navItems: [
      { path: "/pricing", label: "Pricing" },
      { path: "/help", label: "Help" },
    ],
    footer: {
      companyName: "IdeasByScott",
      companyDescription: "Building practical tools for real-world problems.",
      productLinks: [
        { label: "Pricing", href: "/pricing" },
        { label: "Help", href: "/help" },
      ],
      companyLinks: [
        { label: "About", href: "/about" },
        { label: "Contact", href: "https://byscott.io/#contact" },
      ],
      legalLinks: [
        { label: "Privacy Policy", href: "https://byscott.io/privacy-policy" },
        { label: "Terms of Service", href: "https://byscott.io/terms" },
      ],
      copyright: `© ${new Date().getFullYear()} IdeasByScott. All rights reserved.`,
    },
  },
  logo: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
});

// BillingByScott configuration
const BILLING_API_URL = window.ENV?.BILLINGBYSCOTT_API_URL || "http://localhost:5004/client_api";
const BILLING_API_KEY = window.ENV?.BILLINGBYSCOTT_API_KEY || "";

configureBilling({
  apiUrl: BILLING_API_URL,
  apiKey: BILLING_API_KEY,
});

function BillingWrapper({ children }) {
  const { currentContainer } = useAuth();

  return (
    <BillingProvider
      apiUrl={BILLING_API_URL}
      apiKey={BILLING_API_KEY}
      getExternalId={() => currentContainer?.id}
      loadOnMount={!!currentContainer}
    >
      {children}
    </BillingProvider>
  );
}

function PageTracker() {
  const location = useLocation();
  usePageTracking(location.pathname);
  return null;
}

// Sync the authenticated user's identity (user_id / member_id / container_id)
// into the metrics client. Uses the packaged hook so every app wires this the
// same one-liner way; boot-seed (application.js) covers the pre-/me window.
function MetricsIdentity() {
  useMetricsIdentity(useAuth());
  return null;
}

function ProtectedRoute({ children, requireAdmin = false }) {
  return (
    <AuthProtectedRoute requiredRoles={requireAdmin ? ["owner", "admin"] : undefined}>
      <BillingWrapper>
        <Layout>{children}</Layout>
      </BillingWrapper>
    </AuthProtectedRoute>
  );
}

// Minimal apiClient adapter for the shared @byscott-io/widgetsbyscott-client
// Dashboard + WidgetDetail components. Points at metrics' own JWT-
// authenticated proxy, which forwards to widgets.byscott.io using the
// ConsumerApp api_key on the server.
const widgetsApiClient = {
  list: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(`/api/dashboard/embedded_widgets${qs ? `?${qs}` : ""}`, {
      headers: widgetsAuthHeaders(),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  },
  show: async (idOrSlug) => {
    const res = await fetch(`/api/dashboard/embedded_widgets/${encodeURIComponent(idOrSlug)}`, {
      headers: widgetsAuthHeaders(),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  },
};

function widgetsAuthHeaders() {
  const h = { Accept: "application/json" };
  const t = storage.getToken?.();
  const a = storage.getContainerId?.();
  if (t) h.Authorization = `Bearer ${t}`;
  if (a) h["X-Account-Id"] = a;
  return h;
}

function WidgetsAppProvider({ children }) {
  return (
    <WidgetsProvider
      apiClient={widgetsApiClient}
      widgetPath={(id) => `/app/widgets/${id}`}
    >
      {children}
    </WidgetsProvider>
  );
}

function SpaLinkInterceptor({ children }) {
  useSpaLinkInterceptor();
  return children;
}

// TWO BOUNDARIES, doing different jobs. This inner one is inside the router and
// resets on navigation, so a page that throws is recovered by going elsewhere.
// The outer one sits OUTSIDE the providers, so it also catches a crash in a
// provider, which this one cannot -- it mounts underneath them. Without either,
// an uncaught render error unmounts the tree and leaves a blank page.
function RouteBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// A deploy announces itself over waldo when the new container boots. Core must
// not import the waldo client -- not every app has one -- so the app turns the
// broadcast into the window event core listens for. The payload is not used: it
// only prompts a fresh ask of /api/version, which is what decides.
function VersionAnnouncements() {
  useWaldoEvent('app_version', () => {
    window.dispatchEvent(new Event(VERSION_ANNOUNCED_EVENT));
  });
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <PresenceProvider>
        <VersionAnnouncements />
        <WidgetStyles />
        <UpdateAvailableBanner />
        <PageTracker />
        <MetricsIdentity />
        <SpaLinkInterceptor>
        <RouteBoundary>
        <Routes>
          {/* Marketing Pages */}
          <Route
            path="/"
            element={
              <MarketingRoute>
                <LandingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <MarketingRoute>
                <PricingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/help/*"
            element={
              <MarketingRoute>
                <HelpPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/about"
            element={
              <MarketingRoute>
                <AboutPage />
              </MarketingRoute>
            }
          />

          {/* Auth routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginForm />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterForm />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordForm />
              </PublicRoute>
            }
          />
          <Route path="/invitations/accept/:token" element={<AcceptInvitationPage />} />

          {/* App routes (protected) */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/values"
            element={
              <ProtectedRoute>
                <Values />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/referrals"
            element={
              <ProtectedRoute>
                <ReferralFunnel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/visitors"
            element={
              <ProtectedRoute>
                <Visitors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/explore"
            element={
              <ProtectedRoute>
                <Explore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps"
            element={
              <ProtectedRoute requireAdmin>
                <Apps />
              </ProtectedRoute>
            }
          />

          {/* Widgets — thin wrappers over the shared Dashboard and
              WidgetDetail components; data comes from metrics' proxy
              against widgets.byscott.io. */}
          <Route
            path="/app/widgets"
            element={
              <ProtectedRoute>
                <WidgetsAppProvider>
                  <WidgetsDashboard header={<h1 className="text-2xl font-semibold mb-4">Widgets</h1>} />
                </WidgetsAppProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/widgets/:id"
            element={
              <ProtectedRoute>
                <WidgetsAppProvider>
                  <WidgetDetailView
                    rootCrumb={{ label: "Widgets", to: "/app/widgets", state: {} }}
                  />
                </WidgetsAppProvider>
              </ProtectedRoute>
            }
          />

          {/* Account switcher */}
          <Route
            path="/app/accounts"
            element={
              <ProtectedRoute>
                <ContainersPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/app/admin"
            element={<Navigate to="/app/admin/team" replace />}
          />
          <Route
            path="/app/admin/:tab"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/admin/:tab/:section"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/* Profile and Support */}
          <Route
            path="/app/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteBoundary>
        </SpaLinkInterceptor>
        <PublicChat
          authenticatedTask={{
            taskType: "MetricsChatTask",
            title: "Metrics AI",
            placeholder: "Ask about your metrics...",
            welcomeMessage: "Hi! Ask me about your metrics, events, or data analysis.",
            voiceEnabled: true,
            attachmentsEnabled: true,
          }}
          anonymousTask={{
            taskType: "SupportChatTask",
            title: "MetricsByScott AI",
            welcomeMessage:
              "Hi! I can help you figure out if MetricsByScott fits your tracking needs vs Mixpanel/GA. What are you trying to track?",
            placeholder: "Ask about events, integrations, vs Mixpanel…",
          }}
        />
        </PresenceProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
